import Tabs from "components/EvtKit/tabs/Tabs"
import TabsContent from "components/EvtKit/tabs/TabsContent"
import ProductsBenefits from "./ProductsBenefits"
import PageLayout from "components/PageLayout"
import { productFormHeadingText, submitionError, submitionSuccess, tabIds } from "../../constants/defaultValues"
import { useQueryState } from "context/queryState"
import { useAppDispatch, useAppSelector } from "reducers/store"
import BodyText from "components/Typography/BodyText"
import { memo, useCallback, useEffect } from "react"
import styled from "styled-components"
import { useNavigate, useParams } from "react-router-dom"
import { useQueryClient } from "react-query"
import { showToast } from "reducers/toast/toast-actions"
import ApiProducts from "api/ApiProducts"
import { PRODUCT_INFO } from "reducers/products/actions-types"
import { useProductIdentifiers } from "../../hooks/useProductIdentifiers"
import useProduct from "../../hooks/useProduct"
import useProductRequest from "../../hooks/useProductRequest"
import { mapProductToSubmition } from "../../mappers/mapProductForm"
import ProductForm from "../../modals/ProductForm"
import IsExternal from "../../modals/IsExternal"
import IsFederated from "../../modals/IsFederated"
import IntegrationForm from "../../modals/IntegrationForm"
import CreateProductTabList from "./tabs/CreateProductTabList"
import NavigateButtons from "./NavigateButtons"
import ProductBannerModal from "./modal/ProductBannerModal/ProductBannerModal"
import Heading from "components/Typography/Heading"

const ContentContainer = styled.div`
  display: flex;
  width: 98%;
  gap: 8px;
  margin: 43px 0 20px 0;
`

const CardContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: ${(props) => props.theme.palette.white};
  padding: 16px 16px 0px 16px;
  box-shadow: 0px 2px 6px 0px #0000001a;
`

const ErrorContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 48px;
`

const SeparatorLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.palette.mediumDarkGray};
  opacity: 0.3;
  margin-top: 12px;
  margin-bottom: 28px;
`

const ProductUpdate = () => {
  const { productParamsId } = useParams()

  const productId = productParamsId ?? ""

  useQueryState(
    [PRODUCT_INFO, productId],
    async () => await new ApiProducts().getProductById(productId),
    PRODUCT_INFO,
    {
      refetchOnMount: "always",
      retry: 0
    }
  )

  const { data, loading, error } = useAppSelector((state) => state.product.productInfo)

  const { identifiersDefinition, identifiersCleanup } = useProductIdentifiers()

  const appDispatch = useAppDispatch()

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const { isValidProduct, productToSubmit, formData, benefits, overWriteProductData } = useProduct()

  const { productForm, bannerInfo, isFederatedForm, isExternalForm, integrationForm, productInfo } = formData

  const onSuccess = useCallback(() => {
    appDispatch(showToast(submitionSuccess.update, "success"))

    navigate("/products")
    queryClient.invalidateQueries(PRODUCT_INFO)
    queryClient.invalidateQueries(productId)
  }, [productId])

  const { onSubmit, isLoading } = useProductRequest("update", onSuccess, submitionError.update)

  const handleSubmit = useCallback(() => {
    const productData = mapProductToSubmition(productToSubmit.data, benefits.benefitsList)

    onSubmit(productData)
  }, [onSubmit, productToSubmit.data, benefits.benefitsList])

  useEffect(() => {
    if (!data.id) return

    overWriteProductData(data)

    identifiersDefinition(data.id, data.prefixUrl)
  }, [data])

  useEffect(() => {
    window.scrollTo(0, 0)

    return () => {
      identifiersCleanup()
    }
  }, [])

  return (
    <PageLayout title="Produtos" isLoading={loading}>
      <ContentContainer>
        <Tabs defaultActiveTabId={tabIds.product}>
          {!loading && !error && (
            <>
              <CardContainer>
                <CreateProductTabList isBenefitStepDisabled={!isValidProduct} isUpdate />
                <TabsContent triggerId={tabIds.product}>
                  <ProductForm headingText={productFormHeadingText.update} {...productForm} {...productInfo} isUpdate />
                  <SeparatorLine />
                  <Heading variant="h6">
                    Ative as opções que desejar, fornecendo as informações necessárias para cada campo:
                  </Heading>
                  <IsFederated {...productInfo} {...isFederatedForm} />
                  <IsExternal {...isExternalForm} />
                  <IntegrationForm {...integrationForm} modalType="update" />
                  <NavigateButtons isValidProductForm={isValidProduct} />
                </TabsContent>
                <TabsContent triggerId={tabIds.benefits}>
                  <ProductsBenefits {...benefits} isLoading={isLoading} onSubmit={handleSubmit} />
                </TabsContent>
              </CardContainer>
              <ProductBannerModal {...bannerInfo} />
            </>
          )}
          {!loading && error && (
            <CardContainer>
              <CreateProductTabList isBenefitStepDisabled />
              <TabsContent triggerId={tabIds.product}>
                <ErrorContainer>
                  <BodyText>Produto não encontrado!</BodyText>
                </ErrorContainer>
              </TabsContent>
            </CardContainer>
          )}
        </Tabs>
      </ContentContainer>
    </PageLayout>
  )
}

export default memo(ProductUpdate)
